import { useState } from 'react';
import { X, Image as ImageIcon } from 'lucide-react';
import type { ImageFolder } from '../../types/images';
import ImageManager from './ImageManager';
import Button from '../../components/Button';

interface ImageInsertDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onInsert: (imageUrl: string, altText: string) => void;
    folder: ImageFolder;
    title?: string;
}

export default function ImageInsertDialog({
    isOpen,
    onClose,
    onInsert,
    folder,
    title = "Insert Image"
}: ImageInsertDialogProps) {
    const [showImageManager, setShowImageManager] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [altText, setAltText] = useState('');
    const [urlInput, setUrlInput] = useState('');
    const [useUrlInput, setUseUrlInput] = useState(false);

    const handleImageSelect = (url: string) => {
        setSelectedImageUrl(url);
        setShowImageManager(false);

        // Auto-generate alt text from filename
        try {
            const filename = url.split('/').pop()?.split('?')[0] || '';
            const nameWithoutExt = filename.split('.')[0];
            const autoAltText = nameWithoutExt
                .replace(/[-_]/g, ' ')
                .replace(/^\d+-/, '') // Remove timestamp prefix
                .trim();
            setAltText(autoAltText);
        } catch {
            setAltText('Image');
        }
    };

    const handleInsert = () => {
        const imageUrl = useUrlInput ? urlInput : selectedImageUrl;
        if (!imageUrl.trim()) return;

        const finalAltText = altText.trim() || 'Image';
        onInsert(imageUrl, finalAltText);

        // Reset state
        setSelectedImageUrl('');
        setAltText('');
        setUrlInput('');
        setUseUrlInput(false);
        onClose();
    };

    const handleCancel = () => {
        setSelectedImageUrl('');
        setAltText('');
        setUrlInput('');
        setUseUrlInput(false);
        onClose();
    };

    const currentImageUrl = useUrlInput ? urlInput : selectedImageUrl;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-[70] px-4 py-6 sm:py-20">
            <div className="relative mx-auto p-0 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                {/* Header */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">{title}</h3>
                    <Button
                        onClick={handleCancel}
                        variant="ghost"
                        icon={<X size={24} />}
                    />
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 space-y-6">
                    {/* Image Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Select Image Source
                        </label>

                        <div className="flex flex-wrap gap-3 mb-4">
                            <Button
                                onClick={() => setShowImageManager(true)}
                                variant={!useUrlInput ? "primary" : "outline"}
                                size="sm"
                                icon={<ImageIcon size={16} />}
                            >
                                Browse Library
                            </Button>
                            <Button
                                onClick={() => setUseUrlInput(!useUrlInput)}
                                variant={useUrlInput ? "primary" : "outline"}
                                size="sm"
                            >
                                {useUrlInput ? "Using URL" : "Use URL"}
                            </Button>
                        </div>

                        {useUrlInput && (
                            <div className="space-y-2">
                                <input
                                    type="url"
                                    value={urlInput}
                                    onChange={(e) => setUrlInput(e.target.value)}
                                    placeholder="https://example.com/image.jpg"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        )}
                    </div>

                    {/* Image Preview */}
                    {currentImageUrl && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Preview
                            </label>
                            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                <div className="aspect-video bg-white rounded flex items-center justify-center max-h-64">
                                    <img
                                        src={currentImageUrl}
                                        alt="Preview"
                                        className="max-w-full max-h-full object-contain rounded"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.style.display = 'none';
                                            target.parentElement!.innerHTML = `
                        <div class="flex flex-col items-center justify-center text-gray-400 p-8">
                          <svg class="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                          <span class="text-sm">Failed to load image</span>
                        </div>
                      `;
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Alt Text Input */}
                    <div>
                        <label htmlFor="altText" className="block text-sm font-medium text-gray-700 mb-2">
                            Alt Text (for accessibility)
                        </label>
                        <input
                            type="text"
                            id="altText"
                            value={altText}
                            onChange={(e) => setAltText(e.target.value)}
                            placeholder="Describe the image..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Alt text helps screen readers describe the image to visually impaired users.
                        </p>
                    </div>

                    {/* Markdown Preview */}
                    {currentImageUrl && altText && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Markdown Output
                            </label>
                            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                                <code className="text-sm font-mono text-gray-800 break-all">
                                    ![{altText}]({currentImageUrl})
                                </code>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
                    <Button
                        onClick={handleCancel}
                        variant="outline"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleInsert}
                        variant="primary"
                        disabled={!currentImageUrl.trim()}
                    >
                        Insert Image
                    </Button>
                </div>
            </div>

            {/* Image Manager Modal */}
            <ImageManager
                isOpen={showImageManager}
                onClose={() => setShowImageManager(false)}
                onSelectImage={handleImageSelect}
                currentFolder={folder}
                title={`Select Image for ${folder.charAt(0).toUpperCase() + folder.slice(1)}`}
            />
        </div>
    );
} 